import type { FullOrder, Mode, Release } from "./types";

// What the Orders screens work out from get_order: an order's cost and margin, and what needs a look on each release.
// Pure, so it is tested without a browser.

export interface LineFigures {
  lineNo: number;
  sku: string;
  customerItemNo: string | null;
  description: string | null;
  qtyEa: number;
  price: number;
  /** The supplier's cost per EA from the vendor PO line for this SKU; undefined when no vendor PO has it. */
  cost?: number;
  vendorPartNo?: string | null;
  supplier?: string;
  sell: number;
  costValue?: number;
  gmPct?: number;
}

/** GM below this is marked on a line (the agreed Order screen: "GM below 15% is marked"). */
export const LOW_GM_PCT = 15;

export function lineFigures(order: FullOrder): LineFigures[] {
  const costs = new Map<string, { cost: number; vendorPartNo: string | null; supplier: string }>();
  for (const v of order.vendorPos) {
    if (v.status === "CLOSED") continue;
    for (const l of v.lines ?? []) {
      // no unitCost: the caller doesn't hold costs.read, and the line is shown without cost or margin
      if (typeof l.unitCost === "number") costs.set(l.sku, { cost: l.unitCost, vendorPartNo: l.vendorPartNo, supplier: v.supplier?.name ?? "" });
    }
  }
  return order.lines.map((l) => {
    const c = costs.get(l.sku);
    const sell = l.qtyEa * l.unitPrice;
    const costValue = c ? l.qtyEa * c.cost : undefined;
    return {
      lineNo: l.lineNo, sku: l.sku, customerItemNo: l.customerItemNo, description: l.description, qtyEa: l.qtyEa,
      price: l.unitPrice, cost: c?.cost, vendorPartNo: c?.vendorPartNo, supplier: c?.supplier, sell, costValue,
      gmPct: costValue !== undefined && sell ? ((sell - costValue) / sell) * 100 : undefined,
    };
  });
}

export interface OrderFigures { sell: number; cost?: number; gm?: number; gmPct?: number }

/** Totals; cost and margin only when every line has a cost (else they'd mislead). */
export function orderFigures(lines: LineFigures[]): OrderFigures {
  const sell = lines.reduce((s, l) => s + l.sell, 0);
  if (!lines.length || lines.some((l) => l.costValue === undefined)) return { sell };
  const cost = lines.reduce((s, l) => s + (l.costValue ?? 0), 0);
  return { sell, cost, gm: sell - cost, gmPct: sell ? ((sell - cost) / sell) * 100 : undefined };
}

export type Tone = "late" | "soon" | "good" | "neutral";

/** The release's day: confirmed over planned, delivery for deliveries, ship for pickups. */
export function releaseDay(r: Release): string | null {
  return r.confirmedDelivery ?? r.plannedDelivery ?? r.confirmedShip ?? r.plannedShip ?? null;
}

const DONE = ["SHIPPED", "DELIVERED", "INVOICED", "RECONCILED"];

/**
 * What needs a look on a release, in words (never colour alone), or null. `today` is New York's date (YYYY-MM-DD);
 * `supplier` names who should confirm.
 */
export function releaseFlag(r: Release, today: string, supplier: string): { text: string; tone: Tone } | null {
  if (r.status === "WITHDRAWN") return null;
  if (r.status === "EXCEPTION") return { text: "Exception open", tone: "late" };
  const shipped = DONE.includes(r.status) || !!r.shippedOn;
  const ship = r.confirmedShip ?? r.plannedShip;
  if (!shipped && ship && ship < today) return { text: "Past its date, not marked shipped", tone: "late" };
  if (!shipped && r.closedDay) return { text: `${r.closedDay}: move it?`, tone: "soon" };
  if (!shipped && !r.vendorConfirmedAt && ship && daysBetween(today, ship) <= 7) {
    return { text: `${supplier || "Supplier"} to confirm`, tone: "soon" };
  }
  return null;
}

function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T12:00:00Z`) - Date.parse(`${a}T12:00:00Z`)) / 86_400_000);
}

/**
 * Which rows a screen shows: the mode asked for in its address (?mode=SIMULATION), else the platform's own. Asking
 * for the other mode lets a person look at simulation copies during a LIVE rehearsal (or live rows while simulating);
 * acts still follow the platform's mode, so the platform refuses them on the other mode's rows.
 */
export function viewMode(query: URLSearchParams, platformMode: Mode): { mode: Mode; other: boolean; param: string } {
  const asked = query.get("mode");
  const mode: Mode = asked === "LIVE" || asked === "SIMULATION" ? asked : platformMode;
  const other = mode !== platformMode;
  return { mode, other, param: other ? `?mode=${mode}` : "" };
}

/** A status code in words: CUSTOMER_APPROVED -> "Customer approved". */
export function words(code: string | null | undefined): string {
  const s = (code ?? "").toLowerCase().replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}
